import { useAuthContext } from '@/context/auth/useAuthContext';
import { HStack, Link as ChakraLink, Text, VStack } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import {
  creatorProfileMenuOption,
  employeeRoleMenuOptions,
  orgAdminRoleMenuOptions,
  peerSupportRoleMenuOptions,
  superAdminMenuOptions,
} from '../options';
import type { TMenuOption } from '../options';
import { Link, useRouter } from '@tanstack/react-router';
import menuMessages from '@/messages/menu';
import { background } from '@/theme/colors/palettes';
import VerifyEmailConfirmationModal from '@/components/molecules/VerifyEmailConfirmationModal';
import roles from '@/constants/roles';
import sharedRoutes from '@/constants/routes/shared';
import { employeeUserRoutes } from '@/constants/routes/app';
import { useAppStore } from '@/store/useAppStore';
import { hasUnacceptedNonContentPolicyDocument } from '@/utils/onboardingUtils';

interface Props {
  onPressCallback?: () => void;
}

const ProfileMenu = ({ onPressCallback = () => {} }: Props) => {
  const { isAdmin, isPeerSupport, isContentCreator, isSuperAdmin, signOut, user } = useAuthContext();
  const legalAcceptanceStatuses = useAppStore((state) => state.legalAcceptanceStatuses);

  const {
    state: {
      location: { pathname },
    },
    navigate,
  } = useRouter();

  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);

  const isOnboarding = pathname.includes('/onboarding');
  const hasPendingEulaUpdate = hasUnacceptedNonContentPolicyDocument(legalAcceptanceStatuses);
  const isContentCreatorOnly = isContentCreator && user?.roles.length === 1;
  const canBecomeCreator =
    !isContentCreator && (isAdmin || isPeerSupport || !!user?.roles.includes(roles.peerSupportLead));

  const secondaryOptions = useMemo(() => {
    if (isOnboarding || isSuperAdmin || hasPendingEulaUpdate) return [];

    const options = [
      ...(isContentCreator ? [creatorProfileMenuOption] : []),
      ...employeeRoleMenuOptions
        .slice(1)
        .filter((option) => !isContentCreatorOnly || option.navigateTo !== employeeUserRoutes.userProfile),
    ];
    if (!canBecomeCreator) return options;

    const settingsIndex = options.findIndex((option) => option.navigateTo === sharedRoutes.settings);
    const becomeCreatorOption = {
      label: menuMessages.becomeCreator,
      navigateTo: sharedRoutes.creatorApply,
    };

    if (settingsIndex === -1) return [...options, becomeCreatorOption];

    return [...options.slice(0, settingsIndex + 1), becomeCreatorOption, ...options.slice(settingsIndex + 1)];
  }, [isOnboarding, isSuperAdmin, hasPendingEulaUpdate, isContentCreator, isContentCreatorOnly, canBecomeCreator]);

  // Note: If it is onboarding or eula update flow, alow user only to sign out
  const menuOptions = useMemo(() => {
    let finalOptions = isContentCreatorOnly ? [] : [employeeRoleMenuOptions[0]];

    if (isOnboarding || hasPendingEulaUpdate) return [];

    if (isSuperAdmin) return superAdminMenuOptions;

    if (isPeerSupport) finalOptions.push(...peerSupportRoleMenuOptions);

    if (isAdmin) finalOptions.push(...orgAdminRoleMenuOptions);

    if (!isContentCreatorOnly && finalOptions.length === 1) {
      return [{ label: 'Dashboard', navigateTo: employeeRoleMenuOptions[0].navigateTo }];
    }

    return finalOptions;
  }, [
    isOnboarding,
    hasPendingEulaUpdate,
    isAdmin,
    isPeerSupport,
    isContentCreator,
    isSuperAdmin,
    isContentCreatorOnly,
  ]);

  const handleSignOut = () => {
    signOut();
    navigate({
      to: '/sign-in',
    });
  };

  const renderMenuOption = (option: TMenuOption, index: number) => {
    const isActiveRoute = pathname === option.navigateTo;

    const isBecomeACreator = option.navigateTo === '/creator/apply';
    const isEmailVerified = user?.email_verified;

    return (
      <ChakraLink
        as={Link}
        key={`${index}-${option.label}`}
        onClick={(event) => {
          if (isBecomeACreator && !isEmailVerified) {
            event.preventDefault();
            event.stopPropagation();

            setIsVerifyEmailModalOpen(true);
          }

          onPressCallback();
        }}
        to={option.navigateTo}
        textAlign={'right'}
        fontWeight={'600'}
        color={isActiveRoute ? 'primary.500' : 'text.mediumBlue'}
        _hover={{ opacity: 0.7 }}
      >
        {option.label}
      </ChakraLink>
    );
  };

  return (
    <>
      <VStack padding={'20px'} alignItems={'flex-end'}>
        {menuOptions.map(renderMenuOption)}

        {menuOptions.length > 0 && secondaryOptions.length > 0 && (
          <HStack width={'100%'} height={'1px'} backgroundColor={background.separatorBlue} />
        )}

        {secondaryOptions.map(renderMenuOption)}

        <HStack
          onClick={handleSignOut}
          onClickCapture={() => {
            onPressCallback();
          }}
          _hover={{ cursor: 'pointer' }}
        >
          <Text color={'text.mediumGray'} fontWeight={'600'}>
            {menuMessages.signOut}
          </Text>
        </HStack>
      </VStack>

      <VerifyEmailConfirmationModal
        isOpen={isVerifyEmailModalOpen}
        onClose={() => setIsVerifyEmailModalOpen(false)}
        email={user?.email}
      />
    </>
  );
};

export default ProfileMenu;
